import {
  Button,
  Checkbox,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Skeleton,
  SkeletonItem,
  Spinner,
  Switch,
  Text,
  mergeClasses,
} from "@fluentui/react-components";
import {
  ArrowDownload24Regular,
  ChevronDown24Regular,
  ChevronUp24Regular,
  Delete24Regular,
  Dismiss16Regular,
  Dismiss24Regular,
  MoreVertical24Regular,
} from "@fluentui/react-icons";
import {
  addProjectIqWorkspaceDocument,
  bulkDeleteProjectIqWorkspaceDocuments,
  bulkSetWorkspaceDocumentShareStatus,
  bulkSetWorkspaceDocumentType,
  createProjectIqWorkspaceFolder,
  deleteProjectIqWorkspaceDocument,
  deleteProjectIqWorkspaceFolder,
  getProjectIqContextFileTags,
  getProjectIqWorkspaceDocuments,
  getProjectIqWorkspaceFolders,
  getProjectIqWorkspaceIndexingStatus,
  getProjectIqWorkspaces,
  setWorkspaceDocumentShareStatus,
  setWorkspaceDocumentType,
} from "apps/project-iq/api";
import { ALLOWED_FILE_EXTENSIONS, ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { DocumentIcon } from "icons/DocumentIcon";
import { FolderEmptyIcon } from "icons/FolderEmptyIcon";
import { FolderIcon } from "icons/FolderIcon";
import { IContextFileTag } from "models/IContextFileTag";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { updateProjectIqWorkspace as updateWorkspaceAction } from "redux/projectIqWorkspacesSlice";
import {
  downloadProjectIqWorkspaceDocument,
  safeDecodeURIComponent,
  uploadFilesToStorage,
} from "utils/blobUtils";
import {
  getFileTypeInfo,
  getDisplayFilename,
} from "apps/project-iq/utils/fileUtils";
import { useSharedPillButtonStyles } from "theme/sharedStyles";
import {
  DocumentGlyphIcon,
  EyeIcon,
  PlusIcon,
  ShareIcon,
  TrashIcon,
  UploadIcon,
} from "apps/project-iq/components/icons/ProjectIqIcons";
import { ContextFilesBreadcrumb } from "./ContextFilesBreadcrumb";
import { DocumentTypeTag } from "./DocumentTypeTag";
import { NewFolderRow } from "./NewFolderRow";
import {
  getDocumentTypeDotClassName,
  useDocumentTypeTagStyles,
} from "./DocumentTypeTag.styles";
import {
  ERROR_BANNER_AUTO_DISMISS_DELAY_MS,
  FAILED_UPLOAD_AUTO_DISMISS_DELAY_MS,
  useStyles,
} from "./WorkspaceDocuments.styles";
import {
  DocumentsSaveStatus,
  DocumentTypeFilter,
  IFolderUploadBatch,
  IUploadingFile,
  IWorkspaceDocument,
  IWorkspaceDocumentsProps,
} from "./WorkspaceDocuments.types";

const SHARE_COLUMN_HEADER_ID = "workspace-documents-share-column-header";
const SAVE_IDLE_DELAY_MS = 2000;
const FOLDER_UPLOAD_SUCCESS_CLEAR_DELAY_MS = 800;
const ROOT_FOLDER_PATH_SEGMENT_INDEX = 0;
const SKELETON_ROW_COUNT = 3;
const SKELETON_FILTER_PILL_WIDTHS = [40, 112, 88, 128, 108, 60, 128];
export const WorkspaceDocuments = (
  props: IWorkspaceDocumentsProps,
): JSX.Element => {
  const styles = useStyles();
  const pillButtonStyles = useSharedPillButtonStyles();
  const documentTypeTagStyles = useDocumentTypeTagStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const uploadMenuButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useDispatch();

  const [uploadingFiles, setUploadingFiles] = useState<IUploadingFile[]>([]);
  const [folderUploadBatch, setFolderUploadBatch] =
    useState<IFolderUploadBatch | null>(null);
  const [
    isFolderUploadFailureListExpanded,
    setIsFolderUploadFailureListExpanded,
  ] = useState(false);
  const [documents, setDocuments] = useState<IWorkspaceDocument[]>([]);
  const [allFolderPaths, setAllFolderPaths] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<IContextFileTag[]>([]);
  const [activeTypeFilter, setActiveTypeFilter] =
    useState<DocumentTypeFilter>("all");
  const [currentFolderPath, setCurrentFolderPath] = useState<string>("");
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [dropTargetFolderPath, setDropTargetFolderPath] = useState<
    string | null
  >(null);
  const [selectedFilenames, setSelectedFilenames] = useState<Set<string>>(
    new Set(),
  );
  const [deletingFilenames, setDeletingFilenames] = useState<Set<string>>(
    new Set(),
  );
  const [deletingFolderPaths, setDeletingFolderPaths] = useState<Set<string>>(
    new Set(),
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const isDocumentsLoadedRef = useRef(false);
  const isFoldersLoadedRef = useRef(false);
  const [deleteConfirmDoc, setDeleteConfirmDoc] =
    useState<IWorkspaceDocument | null>(null);
  const [deleteConfirmFolder, setDeleteConfirmFolder] = useState<string | null>(
    null,
  );
  const [bulkDeleteConfirmation, setBulkDeleteConfirmation] = useState(false);
  const [pendingBulkShareAction, setPendingBulkShareAction] = useState<
    "share" | "unshare" | null
  >(null);
  const [pendingShareToggle, setPendingShareToggle] = useState<{
    filename: string;
  } | null>(null);
  const [isSharingInFlight, setIsSharingInFlight] = useState(false);
  const [isIndexing, setIsIndexing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<DocumentsSaveStatus>("idle");
  const [bulkActionMessage, setBulkActionMessage] = useState("");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    props.onSaveStatusChange?.(saveStatus, bulkActionMessage || undefined);
  }, [saveStatus, bulkActionMessage, props.onSaveStatusChange]);

  useEffect(() => {
    props.onLoadingChange?.(isLoading);
  }, [isLoading, props.onLoadingChange]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const status = await getProjectIqWorkspaceIndexingStatus({
          workspaceId: props.workspaceId,
        });
        if (status.isIndexed) {
          setIsIndexing(false);
          stopPolling();
        }
      } catch {
        // Silently continue polling on error
      }
    }, 5000);
  }, [props.workspaceId, stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  useEffect(() => {
    if (isIndexing) {
      startPolling();
    }
  }, [isIndexing, startPolling]);

  const fetchDocuments = useCallback(async () => {
    try {
      const docs = await getProjectIqWorkspaceDocuments({
        workspaceId: props.workspaceId,
      });
      const visibleDocs = (docs || []).filter(
        (doc: IWorkspaceDocument) => doc.source !== "kc",
      );
      setDocuments(visibleDocs);
    } catch {
      setErrorMessage("Failed to load documents.");
    }
  }, [props.workspaceId]);

  const fetchFolders = useCallback(async () => {
    try {
      const folderList = await getProjectIqWorkspaceFolders({
        workspaceId: props.workspaceId,
      });
      setAllFolderPaths(folderList || []);
    } catch {
      setErrorMessage("Failed to load folders.");
    }
  }, [props.workspaceId]);

  const completeInitialLoadStep = useCallback((markLoaded: () => void) => {
    markLoaded();
    if (isDocumentsLoadedRef.current && isFoldersLoadedRef.current) {
      hasLoadedOnceRef.current = true;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedOnceRef.current) setIsLoading(true);
    fetchDocuments().finally(() => {
      if (!hasLoadedOnceRef.current) {
        completeInitialLoadStep(() => {
          isDocumentsLoadedRef.current = true;
        });
      }
    });
    fetchFolders().finally(() => {
      if (!hasLoadedOnceRef.current) {
        completeInitialLoadStep(() => {
          isFoldersLoadedRef.current = true;
        });
      }
    });
  }, [props.workspaceId]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tags = await getProjectIqContextFileTags();
        setAvailableTags(tags || []);
      } catch {
        // Silently fail tag loading
      }
    };
    void fetchTags();
  }, []);

  useEffect(() => {
    if (!errorMessage) return;
    const timeoutId = setTimeout(() => {
      setErrorMessage("");
    }, ERROR_BANNER_AUTO_DISMISS_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [errorMessage]);

  const clearSelectionAndFilter = useCallback(() => {
    setSelectedFilenames(new Set());
  }, []);

  const handleNavigateToFolder = useCallback(
    (folderPath: string) => {
      setCurrentFolderPath(folderPath);
      clearSelectionAndFilter();
    },
    [clearSelectionAndFilter],
  );

  const pathSegments = useMemo(() => {
    if (activeTypeFilter !== "all") return [];
    return currentFolderPath ? currentFolderPath.split("/") : [];
  }, [currentFolderPath, activeTypeFilter]);

  const handleNavigateToBreadcrumbSegment = useCallback(
    (index: number) => {
      if (index === -1) {
        handleNavigateToFolder("");
      } else if (index >= 0 && index < pathSegments.length) {
        handleNavigateToFolder(pathSegments.slice(0, index + 1).join("/"));
      }
    },
    [pathSegments, handleNavigateToFolder],
  );

  const handleNavigateUp = useCallback(() => {
    if (pathSegments.length > 0) {
      handleNavigateToFolder(pathSegments.slice(0, -1).join("/"));
    }
  }, [pathSegments, handleNavigateToFolder]);

  const currentFolderDocs = useMemo(() => {
    if (!currentFolderPath) {
      return documents.filter((doc) => !doc.filename.includes("/"));
    }
    const prefix = `${currentFolderPath}/`;
    return documents.filter((doc) => {
      if (!doc.filename.startsWith(prefix)) return false;
      const remainder = doc.filename.slice(prefix.length);
      return !remainder.includes("/");
    });
  }, [documents, currentFolderPath]);

  const folderNames = useMemo(() => {
    if (activeTypeFilter !== "all") return [];
    if (!currentFolderPath) {
      return allFolderPaths.filter((path) => !path.includes("/"));
    }
    const prefix = `${currentFolderPath}/`;
    return allFolderPaths
      .filter((path) => {
        if (!path.startsWith(prefix)) return false;
        const remainder = path.slice(prefix.length);
        return !remainder.includes("/");
      })
      .map((path) => path.slice(prefix.length));
  }, [allFolderPaths, currentFolderPath, activeTypeFilter]);

  const directFileCountByFolderName = useMemo(() => {
    const counts = new Map<string, number>();
    for (const folderName of folderNames) {
      const childPath = currentFolderPath
        ? `${currentFolderPath}/${folderName}`
        : folderName;
      const prefix = `${childPath}/`;
      const count = documents.filter((doc) =>
        doc.filename.startsWith(prefix),
      ).length;
      counts.set(folderName, count);
    }
    return counts;
  }, [documents, folderNames, currentFolderPath]);

  const filteredDocuments = useMemo(() => {
    if (activeTypeFilter === "all") return currentFolderDocs;
    return documents.filter((doc) => doc.documentType === activeTypeFilter);
  }, [documents, currentFolderDocs, activeTypeFilter]);

  const visibleFiles = filteredDocuments;

  const allowedExtensions = ALLOWED_FILE_EXTENSIONS.map((ext) =>
    ext.toLowerCase(),
  );

  const validateFile = (file: File): string | null => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      return `File "${file.name}" is not supported. Allowed: ${ALLOWED_FILE_EXTENSIONS.join(", ")}`;
    }
    return null;
  };

  const uploadFileToFolder = async (
    uploadFile: IUploadingFile,
    folderPath: string,
  ): Promise<boolean> => {
    try {
      const uploaded = await uploadFilesToStorage(
        [{ id: uploadFile.id, file: uploadFile.file }],
        "workspace_library",
        props.workspaceId,
        false,
        undefined,
        folderPath,
      );

      if (uploaded.length > 0) {
        const bareBlobFilename = safeDecodeURIComponent(
          uploaded[0].url?.split("/").pop() || ConstantValues.EMPTY_STRING,
        );
        const blobFilename = folderPath
          ? `${folderPath}/${bareBlobFilename}`
          : bareBlobFilename;

        try {
          await addProjectIqWorkspaceDocument({
            workspaceId: props.workspaceId,
            filename: blobFilename,
            originalFilename: uploadFile.file.name,
            source: "file",
          });
        } catch {
          const serverDocuments = await getProjectIqWorkspaceDocuments({
            workspaceId: props.workspaceId,
          });
          const alreadyPersisted = (serverDocuments || []).some(
            (doc: IWorkspaceDocument) => doc.filename === blobFilename,
          );
          if (!alreadyPersisted) throw new Error("Failed to add document");
        }

        setUploadingFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id
              ? { ...f, status: "uploaded" as const, blobFilename }
              : f,
          ),
        );

        setIsIndexing(true);
        return true;
      }
      return false;
    } catch {
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.id === uploadFile.id ? { ...f, status: "error" as const } : f,
        ),
      );
      setErrorMessage(
        `Failed to upload "${uploadFile.originalFilename}". Please try again.`,
      );
      setTimeout(() => {
        setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadFile.id));
      }, FAILED_UPLOAD_AUTO_DISMISS_DELAY_MS);
      return false;
    }
  };

  const handleDismissFailedUpload = useCallback((uploadFileId: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadFileId));
  }, []);

  const uploadFiles = async (
    files: FileList | File[],
    targetFolderPath: string = currentFolderPath,
  ) => {
    setErrorMessage("");
    const fileArray = Array.from(files);

    const validFiles: File[] = [];
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        setErrorMessage(error);
        return;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    const newFiles: IUploadingFile[] = validFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      originalFilename: file.name,
      status: "uploading" as const,
    }));

    setUploadingFiles((prev) => [...prev, ...newFiles]);

    for (const uploadFile of newFiles) {
      await uploadFileToFolder(uploadFile, targetFolderPath);
      await fetchDocuments();
    }

    setUploadingFiles((prev) => prev.filter((f) => f.status !== "uploaded"));
  };

  const hasDraggedFiles = (event: React.DragEvent) =>
    Array.from(event.dataTransfer.types).includes("Files");

  const handleDragOverTarget = (
    event: React.DragEvent,
    targetFolderPath: string,
  ) => {
    if (!canAcceptDroppedFiles || !hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setDropTargetFolderPath(targetFolderPath);
  };

  const handleDragLeaveTarget = (
    event: React.DragEvent,
    targetFolderPath: string,
  ) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }
    setDropTargetFolderPath((prev) =>
      prev === targetFolderPath ? null : prev,
    );
  };

  const handleDropOnTarget = (
    event: React.DragEvent,
    targetFolderPath: string,
  ) => {
    if (!canAcceptDroppedFiles || !hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setDropTargetFolderPath(null);
    if (event.dataTransfer.files.length > 0) {
      void uploadFiles(event.dataTransfer.files, targetFolderPath);
    }
  };

  const handleUploadFiles = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFolder = () => {
    folderInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadFiles(e.target.files);
      e.target.value = "";
    }
  };

  const uploadFolder = async (files: FileList) => {
    setErrorMessage("");
    setFolderUploadBatch(null);
    setIsFolderUploadFailureListExpanded(false);
    const pickedFiles = Array.from(files) as (File & {
      webkitRelativePath: string;
    })[];

    const hasUnsupportedDirectoryPicker = pickedFiles.some(
      (file) => !file.webkitRelativePath,
    );
    if (hasUnsupportedDirectoryPicker) {
      setErrorMessage(
        "Folder upload is not supported in this browser. Please use Upload files instead.",
      );
      return;
    }

    for (const file of pickedFiles) {
      const error = validateFile(file);
      if (error) {
        setErrorMessage(error);
        return;
      }
    }

    if (pickedFiles.length === 0) return;

    const computeFileFolderPath = (file: {
      webkitRelativePath: string;
    }): string => {
      const relativePathSegments = file.webkitRelativePath.split("/");
      const relativeDirPortion = relativePathSegments.slice(0, -1).join("/");
      return currentFolderPath
        ? `${currentFolderPath}/${relativeDirPortion}`
        : relativeDirPortion;
    };

    const newFiles: IUploadingFile[] = pickedFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      originalFilename: file.name,
      status: "uploading" as const,
      isFromFolderUpload: true,
    }));

    const topLevelFolderName =
      pickedFiles[ROOT_FOLDER_PATH_SEGMENT_INDEX].webkitRelativePath.split("/")[
        ROOT_FOLDER_PATH_SEGMENT_INDEX
      ];

    const batchFileIds = new Set(newFiles.map((f) => f.id));
    setUploadingFiles((prev) => [...prev, ...newFiles]);
    setIsFolderUploadFailureListExpanded(false);
    setFolderUploadBatch({
      folderName: topLevelFolderName,
      totalFileCount: pickedFiles.length,
      completedFileCount: 0,
      failedFilenames: [],
      isComplete: false,
    });

    const distinctFolderPaths = new Set<string>();
    for (const file of pickedFiles) {
      distinctFolderPaths.add(computeFileFolderPath(file));
    }

    const sortedFolderPaths = Array.from(distinctFolderPaths).sort(
      (a, b) => a.split("/").length - b.split("/").length,
    );

    for (const folderPath of sortedFolderPaths) {
      try {
        await createProjectIqWorkspaceFolder({
          workspaceId: props.workspaceId,
          folderPath,
        });
      } catch {
        // Ignore failures where the folder might already exist
      }
    }

    const failedFilenames: string[] = [];

    for (let i = 0; i < pickedFiles.length; i++) {
      const file = pickedFiles[i];
      const uploadFile = newFiles[i];
      const fileFolderPath = computeFileFolderPath(file);

      const hasUploadSucceeded = await uploadFileToFolder(
        uploadFile,
        fileFolderPath,
      );

      if (!hasUploadSucceeded) {
        failedFilenames.push(file.webkitRelativePath);
      }

      setFolderUploadBatch((prev) =>
        prev
          ? { ...prev, completedFileCount: prev.completedFileCount + 1 }
          : prev,
      );
    }

    if (failedFilenames.length > 0) {
      setErrorMessage(
        `Failed to upload ${failedFilenames.length} file(s): ${failedFilenames.join(", ")}. Please try again.`,
      );
    }

    await fetchFolders();
    await fetchDocuments();
    setUploadingFiles((prev) => prev.filter((f) => !batchFileIds.has(f.id)));
    setFolderUploadBatch((prev) =>
      prev ? { ...prev, isComplete: true, failedFilenames } : prev,
    );

    if (failedFilenames.length === 0) {
      setTimeout(() => {
        setFolderUploadBatch((prev) => (prev?.isComplete ? null : prev));
      }, FOLDER_UPLOAD_SUCCESS_CLEAR_DELAY_MS);
    }
  };

  const handleFolderInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadFolder(e.target.files);
    } else {
      setErrorMessage("The selected folder is empty.");
    }
    e.target.value = "";
  };

  const handleCreateFolder = async (name: string) => {
    try {
      const newPath = currentFolderPath ? `${currentFolderPath}/${name}` : name;
      await createProjectIqWorkspaceFolder({
        workspaceId: props.workspaceId,
        folderPath: newPath,
      });
      await fetchFolders();
      setIsCreatingFolder(false);
    } catch {
      setErrorMessage("Failed to create folder.");
      throw new Error("Failed to create folder");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmDoc) return;
    const doc = deleteConfirmDoc;
    setDeleteConfirmDoc(null);
    setDeletingFilenames((prev) => new Set(prev).add(doc.filename));
    try {
      await deleteProjectIqWorkspaceDocument({
        workspaceId: props.workspaceId,
        filename: doc.filename,
      });
      setIsIndexing(true);
      setDocuments((prev) => prev.filter((d) => d.filename !== doc.filename));
    } catch {
      setErrorMessage("Failed to delete document. Please try again.");
    } finally {
      setDeletingFilenames((prev) => {
        const next = new Set(prev);
        next.delete(doc.filename);
        return next;
      });
    }
  };

  const handleConfirmFolderDelete = async () => {
    if (!deleteConfirmFolder) return;
    const fullPathBeingDeleted = deleteConfirmFolder;
    const deletedFolderName = fullPathBeingDeleted.split("/").pop() ?? "";
    setDeleteConfirmFolder(null);
    setDeletingFolderPaths((prev) => new Set(prev).add(fullPathBeingDeleted));
    setSaveStatus("saving");
    setBulkActionMessage(`Deleting "${deletedFolderName}"...`);

    try {
      await deleteProjectIqWorkspaceFolder({
        workspaceId: props.workspaceId,
        folderPath: fullPathBeingDeleted,
      });

      setDocuments((prev) =>
        prev.filter(
          (d) =>
            d.filename !== fullPathBeingDeleted &&
            !d.filename.startsWith(`${fullPathBeingDeleted}/`),
        ),
      );

      if (
        currentFolderPath === fullPathBeingDeleted ||
        currentFolderPath.startsWith(`${fullPathBeingDeleted}/`)
      ) {
        const parentPath = fullPathBeingDeleted
          .split("/")
          .slice(0, -1)
          .join("/");
        handleNavigateToFolder(parentPath);
      }

      await fetchFolders();
      await fetchDocuments();
      finishBulkAction(`Deleted "${deletedFolderName}"`);
    } catch {
      setErrorMessage("Failed to delete folder. Please try again.");
      setSaveStatus("error");
      setBulkActionMessage("");
    } finally {
      setDeletingFolderPaths((prev) => {
        const next = new Set(prev);
        next.delete(fullPathBeingDeleted);
        return next;
      });
    }
  };

  const handleToggleShareWithClient = (doc: IWorkspaceDocument) => {
    if (isSharingInFlight) return;
    setPendingShareToggle({ filename: doc.filename });
  };

  const handleConfirmShareToggle = async () => {
    if (!pendingShareToggle) return;
    const currentDocument = documents.find(
      (d) => d.filename === pendingShareToggle.filename,
    );
    if (!currentDocument) {
      setErrorMessage(
        "This document is no longer available. Please try again.",
      );
      setPendingShareToggle(null);
      return;
    }
    const nextIsSharedWithClient = !currentDocument.isSharedWithClient;
    setIsSharingInFlight(true);
    try {
      const updatedDocument = await setWorkspaceDocumentShareStatus({
        workspaceId: props.workspaceId,
        filename: currentDocument.filename,
        isSharedWithClient: nextIsSharedWithClient,
      });
      setDocuments((prev) =>
        prev.map((d) =>
          d.filename === currentDocument.filename
            ? { ...d, ...updatedDocument }
            : d,
        ),
      );
      setPendingShareToggle(null);
    } catch {
      setErrorMessage("Failed to update sharing status. Please try again.");
    } finally {
      setIsSharingInFlight(false);
    }
  };

  const pendingShareToggleDocument = useMemo(
    () =>
      pendingShareToggle
        ? (documents.find((d) => d.filename === pendingShareToggle.filename) ??
          null)
        : null,
    [documents, pendingShareToggle],
  );

  const handleDownloadDocument = async (doc: IWorkspaceDocument) => {
    try {
      await downloadProjectIqWorkspaceDocument(props.workspaceId, doc);
    } catch {
      setErrorMessage("Failed to download document. Please try again.");
    }
  };

  const refreshWorkspaceAfterSowTag = async (): Promise<void> => {
    try {
      const workspaces: IProjectIQWorkspace[] = await getProjectIqWorkspaces();
      const updated = workspaces.find(
        (w: IProjectIQWorkspace) => w.id === props.workspaceId,
      );
      if (updated) {
        dispatch(updateWorkspaceAction(updated));
      }
    } catch {
      // Silently continue; the next incidental workspace refetch will
      // eventually pick up the current sowAnalysis status.
    }
  };

  const handleSetDocumentType = async (
    doc: IWorkspaceDocument,
    documentType: string | null,
  ) => {
    const previousDocumentType = doc.documentType ?? null;
    setDocuments((prev) =>
      prev.map((d) =>
        d.filename === doc.filename ? { ...d, documentType } : d,
      ),
    );
    setSaveStatus("saving");
    try {
      const updatedDocument = await setWorkspaceDocumentType({
        workspaceId: props.workspaceId,
        filename: doc.filename,
        documentType,
      });
      setDocuments((prev) =>
        prev.map((d) =>
          d.filename === doc.filename ? { ...d, ...updatedDocument } : d,
        ),
      );
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), SAVE_IDLE_DELAY_MS);
      if (documentType === "sow") {
        void refreshWorkspaceAfterSowTag();
      }
    } catch {
      setDocuments((prev) =>
        prev.map((d) =>
          d.filename === doc.filename
            ? { ...d, documentType: previousDocumentType }
            : d,
        ),
      );
      setErrorMessage("Failed to update document type. Please try again.");
      setSaveStatus("error");
    }
  };

  const handleSelectTypeFilter = (filter: DocumentTypeFilter) => {
    setActiveTypeFilter(filter === activeTypeFilter ? "all" : filter);
    clearSelectionAndFilter();
  };

  const handleSelectAllFiles = (checked: boolean | "mixed") => {
    if (checked === true) {
      const filenames = new Set(visibleFiles.map((f) => f.filename));
      setSelectedFilenames(filenames);
    } else {
      setSelectedFilenames(new Set());
    }
  };

  const handleToggleFileSelection = (filename: string, checked: boolean) => {
    setSelectedFilenames((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(filename);
      } else {
        next.delete(filename);
      }
      return next;
    });
  };

  const describeFileCount = (count: number) =>
    `${count} file${count === 1 ? "" : "s"}`;

  const finishBulkAction = (successMessage: string) => {
    setSaveStatus("saved");
    setBulkActionMessage(successMessage);
    setTimeout(() => {
      setSaveStatus("idle");
      setBulkActionMessage("");
    }, SAVE_IDLE_DELAY_MS);
  };

  const handleBulkShareWithClient = async () => {
    const filenames = Array.from(selectedFilenames);
    if (filenames.length === 0) return;

    setSaveStatus("saving");
    setBulkActionMessage(`Sharing ${describeFileCount(filenames.length)}...`);
    try {
      const result = await bulkSetWorkspaceDocumentShareStatus({
        workspaceId: props.workspaceId,
        filenames,
        isSharedWithClient: true,
      });

      setDocuments((prev) =>
        prev.map((d) => {
          const updated = result.updated.find(
            (updatedDoc: IWorkspaceDocument) =>
              updatedDoc.filename === d.filename,
          );
          return updated || d;
        }),
      );

      setSelectedFilenames(new Set());
      setPendingBulkShareAction(null);
      finishBulkAction(
        `Shared ${describeFileCount(result.updated.length)} with client`,
      );

      if (result.notFound && result.notFound.length > 0) {
        setErrorMessage(
          `${result.notFound.length} file(s) were no longer available.`,
        );
      }
    } catch {
      setPendingBulkShareAction(null);
      setErrorMessage("Failed to update sharing status. Please try again.");
      setSaveStatus("error");
      setBulkActionMessage("");
    }
  };

  const handleBulkUnshare = async () => {
    const filenames = Array.from(selectedFilenames);
    if (filenames.length === 0) return;

    setSaveStatus("saving");
    setBulkActionMessage(`Unsharing ${describeFileCount(filenames.length)}...`);
    try {
      const result = await bulkSetWorkspaceDocumentShareStatus({
        workspaceId: props.workspaceId,
        filenames,
        isSharedWithClient: false,
      });

      setDocuments((prev) =>
        prev.map((d) => {
          const updated = result.updated.find(
            (updatedDoc: IWorkspaceDocument) =>
              updatedDoc.filename === d.filename,
          );
          return updated || d;
        }),
      );

      setSelectedFilenames(new Set());
      setPendingBulkShareAction(null);
      finishBulkAction(`Unshared ${describeFileCount(result.updated.length)}`);

      if (result.notFound && result.notFound.length > 0) {
        setErrorMessage(
          `${result.notFound.length} file(s) were no longer available.`,
        );
      }
    } catch {
      setPendingBulkShareAction(null);
      setErrorMessage("Failed to update sharing status. Please try again.");
      setSaveStatus("error");
      setBulkActionMessage("");
    }
  };

  const handleBulkSetType = async (documentType: string | null) => {
    const filenames = Array.from(selectedFilenames);
    if (filenames.length === 0) return;

    setSaveStatus("saving");
    setBulkActionMessage(
      `Updating type for ${describeFileCount(filenames.length)}...`,
    );
    try {
      const result = await bulkSetWorkspaceDocumentType({
        workspaceId: props.workspaceId,
        filenames,
        documentType,
      });

      setDocuments((prev) =>
        prev.map((d) => {
          const updated = result.updated.find(
            (updatedDoc: IWorkspaceDocument) =>
              updatedDoc.filename === d.filename,
          );
          return updated || d;
        }),
      );

      setSelectedFilenames(new Set());
      finishBulkAction(
        `Updated type for ${describeFileCount(result.updated.length)}`,
      );
      if (documentType === "sow") {
        void refreshWorkspaceAfterSowTag();
      }

      if (result.notFound && result.notFound.length > 0) {
        setErrorMessage(
          `${result.notFound.length} file(s) were no longer available.`,
        );
      }
    } catch {
      setErrorMessage("Failed to update document types. Please try again.");
      setSaveStatus("error");
      setBulkActionMessage("");
    }
  };

  const handleBulkDeleteConfirm = async () => {
    const filenames = Array.from(selectedFilenames);
    if (filenames.length === 0) return;

    setBulkDeleteConfirmation(false);
    setSaveStatus("saving");
    setBulkActionMessage(`Deleting ${describeFileCount(filenames.length)}...`);

    try {
      const result = await bulkDeleteProjectIqWorkspaceDocuments({
        workspaceId: props.workspaceId,
        filenames,
      });

      setDocuments((prev) =>
        prev.filter((d) => !result.deleted.includes(d.filename)),
      );

      setSelectedFilenames(new Set());
      setIsIndexing(true);
      finishBulkAction(`Deleted ${describeFileCount(result.deleted.length)}`);

      if (result.notFound && result.notFound.length > 0) {
        setErrorMessage(
          `${result.notFound.length} file(s) were no longer available.`,
        );
      }
    } catch {
      setErrorMessage("Failed to delete documents. Please try again.");
      setSaveStatus("error");
      setBulkActionMessage("");
    }
  };

  const isAllFilesSelected = useMemo(() => {
    return (
      visibleFiles.length > 0 && selectedFilenames.size === visibleFiles.length
    );
  }, [visibleFiles, selectedFilenames]);

  const isSomeFilesSelected = useMemo(() => {
    return (
      selectedFilenames.size > 0 && selectedFilenames.size < visibleFiles.length
    );
  }, [visibleFiles, selectedFilenames]);

  const selectAllCheckboxState = isAllFilesSelected
    ? true
    : isSomeFilesSelected
      ? ("mixed" as const)
      : false;

  const iconStyleMap: Record<string, string> = {
    pdf: styles.fileIconPdf,
    xls: styles.fileIconXls,
    ppt: styles.fileIconPpt,
    default: styles.fileIconDefault,
  };

  const filePickerUploadingFiles = useMemo(
    () => uploadingFiles.filter((f) => !f.isFromFolderUpload),
    [uploadingFiles],
  );

  const isFolderUploadInProgress = Boolean(
    folderUploadBatch && !folderUploadBatch.isComplete,
  );
  const hasUnresolvedFolderUploadFailures = Boolean(
    folderUploadBatch?.isComplete &&
    folderUploadBatch.failedFilenames.length > 0,
  );
  const hasActiveUploads =
    filePickerUploadingFiles.some((f) => f.status === "uploading") ||
    isFolderUploadInProgress;
  const canAcceptDroppedFiles =
    !isLoading && !hasActiveUploads && activeTypeFilter === "all";
  const hasNoContent =
    activeTypeFilter === "all" &&
    folderNames.length === 0 &&
    currentFolderDocs.length === 0 &&
    !isCreatingFolder &&
    filePickerUploadingFiles.length === 0 &&
    !folderUploadBatch;
  const hasNoFilteredResults =
    activeTypeFilter !== "all" && filteredDocuments.length === 0;

  const handleDismissFolderUploadFailures = () => {
    setFolderUploadBatch(null);
    setIsFolderUploadFailureListExpanded(false);
    uploadMenuButtonRef.current?.focus();
  };

  const handleToggleFolderUploadFailureList = () => {
    setIsFolderUploadFailureListExpanded((prev) => !prev);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.root}>
        <div className={styles.contentColumn}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
            onChange={handleFileInputChange}
            className={styles.hiddenFileInput}
            aria-hidden="true"
          />

          <input
            ref={folderInputRef}
            type="file"
            multiple
            onChange={handleFolderInputChange}
            className={styles.hiddenFileInput}
            aria-hidden="true"
            {...{ webkitdirectory: "", directory: "" }}
          />

          {errorMessage && (
            <div className={styles.errorBox}>
              <Text className={styles.errorBoxMessage}>{errorMessage}</Text>
              <Button
                appearance="subtle"
                icon={<Dismiss16Regular />}
                className={styles.errorBoxDismissButton}
                aria-label="Dismiss error"
                onClick={() => setErrorMessage("")}
              />
            </div>
          )}

          {bulkActionMessage ? (
            <div className={styles.indexingBanner}>
              {saveStatus === "saving" && <Spinner size="tiny" />}
              <Text>{bulkActionMessage}</Text>
            </div>
          ) : (
            isIndexing && (
              <div className={styles.indexingBanner}>
                <Spinner size="tiny" />
                <Text>Indexing documents for search...</Text>
              </div>
            )
          )}

          <div>
            <>
              <div className={styles.toolbarRow}>
                <ContextFilesBreadcrumb
                  pathSegments={pathSegments}
                  onNavigateToBreadcrumbSegment={
                    handleNavigateToBreadcrumbSegment
                  }
                  onNavigateUp={handleNavigateUp}
                />
                <div className={styles.toolbarActions}>
                  <Button
                    appearance="outline"
                    icon={<PlusIcon size={15} />}
                    className={styles.uploadButton}
                    onClick={() => setIsCreatingFolder(true)}
                    aria-label="Create new folder"
                    disabled={isLoading || activeTypeFilter !== "all"}
                  >
                    New folder
                  </Button>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        ref={uploadMenuButtonRef}
                        appearance="outline"
                        icon={<UploadIcon size={16} />}
                        className={styles.uploadButton}
                        aria-label="Upload options menu"
                        disabled={
                          isLoading ||
                          hasActiveUploads ||
                          activeTypeFilter !== "all"
                        }
                      >
                        Upload
                      </Button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<DocumentGlyphIcon size={15} />}
                          onClick={handleUploadFiles}
                        >
                          Upload files
                        </MenuItem>
                        <MenuItem
                          icon={<FolderIcon size={15} />}
                          onClick={handleUploadFolder}
                        >
                          Upload folder
                        </MenuItem>
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              </div>

              {isCreatingFolder && (
                <NewFolderRow
                  onCreateFolder={handleCreateFolder}
                  onCancel={() => setIsCreatingFolder(false)}
                />
              )}

              {isLoading && (
                <Skeleton
                  className={styles.typeFilterRow}
                  aria-label="Loading document types"
                >
                  {SKELETON_FILTER_PILL_WIDTHS.map((width) => (
                    <SkeletonItem
                      key={width}
                      className={styles.skeletonFilterPill}
                      style={{ width }}
                    />
                  ))}
                </Skeleton>
              )}

              {(documents.length > 0 || activeTypeFilter !== "all") && (
                <div
                  className={styles.typeFilterRow}
                  role="group"
                  aria-label="Filter by document type"
                >
                  <button
                    type="button"
                    className={mergeClasses(
                      pillButtonStyles.pillButton,
                      activeTypeFilter === "all" &&
                        pillButtonStyles.pillButtonActive,
                    )}
                    onClick={() => handleSelectTypeFilter("all")}
                    aria-pressed={activeTypeFilter === "all"}
                  >
                    All
                  </button>
                  {availableTags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className={mergeClasses(
                        pillButtonStyles.pillButton,
                        activeTypeFilter === tag.id &&
                          pillButtonStyles.pillButtonActive,
                      )}
                      onClick={() => handleSelectTypeFilter(tag.id)}
                      aria-pressed={activeTypeFilter === tag.id}
                    >
                      <span className={styles.typeFilterButtonLabel}>
                        <span
                          className={mergeClasses(
                            documentTypeTagStyles.dot,
                            getDocumentTypeDotClassName(
                              documentTypeTagStyles,
                              tag.id,
                            ),
                          )}
                          aria-hidden="true"
                        />
                        {tag.name}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {selectedFilenames.size > 0 && (
                <div className={styles.bulkActionBar}>
                  <div className={styles.bulkActionBarLeft}>
                    <Text className={styles.bulkActionBarSelectionText}>
                      {selectedFilenames.size} selected
                    </Text>
                  </div>
                  <div className={styles.bulkActionBarActions}>
                    <button
                      type="button"
                      className={styles.bulkActionButton}
                      onClick={() => setPendingBulkShareAction("share")}
                      aria-label="Share selected files with client"
                      disabled={saveStatus === "saving"}
                    >
                      <ShareIcon size={14} />
                      Share with client
                    </button>
                    <button
                      type="button"
                      className={styles.bulkActionButton}
                      onClick={() => setPendingBulkShareAction("unshare")}
                      aria-label="Unshare selected files with client"
                      disabled={saveStatus === "saving"}
                    >
                      <EyeIcon size={14} />
                      Unshare
                    </button>
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <button
                          type="button"
                          className={styles.bulkActionButton}
                          aria-label="Set document type for selected files"
                          disabled={saveStatus === "saving"}
                        >
                          <PlusIcon size={14} />
                          Set type
                        </button>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {availableTags.map((tag) => (
                            <MenuItem
                              key={tag.id}
                              icon={
                                <span
                                  className={mergeClasses(
                                    documentTypeTagStyles.dot,
                                    getDocumentTypeDotClassName(
                                      documentTypeTagStyles,
                                      tag.id,
                                    ),
                                  )}
                                  aria-hidden="true"
                                />
                              }
                              onClick={() => handleBulkSetType(tag.id)}
                            >
                              {tag.name}
                            </MenuItem>
                          ))}
                          <MenuDivider />
                          <MenuItem onClick={() => handleBulkSetType(null)}>
                            Clear type
                          </MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                    <button
                      type="button"
                      className={mergeClasses(
                        styles.bulkActionButton,
                        styles.bulkActionButtonDanger,
                      )}
                      onClick={() => setBulkDeleteConfirmation(true)}
                      aria-label="Delete selected files"
                      disabled={saveStatus === "saving"}
                    >
                      <TrashIcon size={14} />
                      Delete
                    </button>
                    <button
                      type="button"
                      className={styles.bulkActionClearButton}
                      onClick={() => setSelectedFilenames(new Set())}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <div
                className={mergeClasses(
                  styles.fileListCard,
                  dropTargetFolderPath === currentFolderPath &&
                    styles.dropTarget,
                )}
                onDragOver={(event) =>
                  handleDragOverTarget(event, currentFolderPath)
                }
                onDragLeave={(event) =>
                  handleDragLeaveTarget(event, currentFolderPath)
                }
                onDrop={(event) => handleDropOnTarget(event, currentFolderPath)}
                role="list"
                aria-label={
                  activeTypeFilter === "all"
                    ? "Workspace documents and folders"
                    : "Filtered documents"
                }
              >
                <div className={styles.columnHeaderRow}>
                  <div className={styles.checkboxColumnHeader}>
                    <Checkbox
                      className={mergeClasses(
                        styles.selectionCheckbox,
                        styles.compactHeaderCheckbox,
                      )}
                      checked={selectAllCheckboxState}
                      onChange={(_, data) => handleSelectAllFiles(data.checked)}
                      disabled={hasNoContent || hasNoFilteredResults}
                      aria-label="Select all files"
                    />
                  </div>
                  <Text className={styles.fileNameColumnHeader}>File Name</Text>
                  <Text className={styles.typeColumnHeader}>Type</Text>
                  <Text
                    id={SHARE_COLUMN_HEADER_ID}
                    className={styles.shareColumnHeader}
                  >
                    Share with Client
                  </Text>
                  <div className={styles.menuColumnHeader} />
                </div>

                {isLoading && (
                  <div role="status" aria-label="Loading files">
                    {Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => (
                      <Skeleton key={i} className={styles.skeletonRow}>
                        <div className={styles.skeletonCheckboxSpacer} />
                        <SkeletonItem
                          shape="square"
                          className={styles.skeletonIcon}
                        />
                        <div className={styles.skeletonNameCell}>
                          <SkeletonItem className={styles.skeletonName} />
                        </div>
                        <div className={styles.skeletonTypeCell}>
                          <SkeletonItem className={styles.skeletonTypeTag} />
                        </div>
                        <div className={styles.skeletonTrailingSpacer} />
                      </Skeleton>
                    ))}
                  </div>
                )}

                {hasNoContent && !isLoading && (
                  <div className={styles.emptyFolderState}>
                    <FolderEmptyIcon size={28} />
                    <Text weight="semibold">No files yet</Text>
                    <Text>Upload files or create a folder to get started.</Text>
                  </div>
                )}

                {hasNoFilteredResults && (
                  <div
                    className={styles.emptyState}
                    role="status"
                    aria-live="polite"
                  >
                    <Text>No documents match this filter.</Text>
                  </div>
                )}

                {folderNames.map((folderName) => {
                  const totalFileCount =
                    directFileCountByFolderName.get(folderName) ?? 0;
                  const childPath = currentFolderPath
                    ? `${currentFolderPath}/${folderName}`
                    : folderName;
                  const isDeleting = deletingFolderPaths.has(childPath);

                  return (
                    <div
                      key={folderName}
                      role="listitem"
                      data-folder-row
                      className={mergeClasses(
                        styles.folderRow,
                        dropTargetFolderPath === childPath && styles.dropTarget,
                        isDeleting && styles.folderRowDisabled,
                      )}
                      onDragOver={(event) =>
                        handleDragOverTarget(event, childPath)
                      }
                      onDragLeave={(event) =>
                        handleDragLeaveTarget(event, childPath)
                      }
                      onDrop={(event) => handleDropOnTarget(event, childPath)}
                    >
                      <div className={styles.checkboxWrapper}></div>
                      <button
                        type="button"
                        className={styles.folderNavigateButton}
                        onClick={() => handleNavigateToFolder(childPath)}
                        aria-label={`${folderName} folder, ${totalFileCount} files`}
                        title={folderName}
                        disabled={isDeleting}
                      >
                        <div className={styles.folderIconWrapper}>
                          <FolderIcon size={18} />
                        </div>
                        <div className={styles.folderInfo}>
                          <div className={styles.folderName}>{folderName}</div>
                          <div className={styles.folderFileCount}>
                            {isDeleting
                              ? "Deleting..."
                              : `${totalFileCount} ${totalFileCount === 1 ? "file" : "files"}`}
                          </div>
                        </div>
                      </button>
                      <div className={styles.typeTagCell} />
                      {isDeleting ? (
                        <Spinner size="tiny" className={styles.fileSpinner} />
                      ) : (
                        <>
                          <div className={styles.shareToggle} />
                          <Menu>
                            <MenuTrigger disableButtonEnhancement>
                              <Button
                                appearance="subtle"
                                icon={<MoreVertical24Regular />}
                                className={styles.menuButton}
                                aria-label="Folder actions"
                              />
                            </MenuTrigger>
                            <MenuPopover>
                              <MenuList>
                                <MenuItem
                                  icon={<Delete24Regular />}
                                  className={styles.menuItemDanger}
                                  onClick={() =>
                                    setDeleteConfirmFolder(childPath)
                                  }
                                >
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        </>
                      )}
                    </div>
                  );
                })}

                {folderUploadBatch && (
                  <div
                    role="listitem"
                    className={mergeClasses(
                      styles.folderRow,
                      !folderUploadBatch.isComplete && styles.folderRowDisabled,
                      styles.folderUploadBatchRow,
                    )}
                  >
                    <div className={styles.folderUploadBatchSummary}>
                      <div className={styles.checkboxWrapper}></div>
                      <div className={styles.folderIconWrapper}>
                        <FolderIcon size={18} />
                      </div>
                      <div className={styles.folderInfo}>
                        <div className={styles.folderName}>
                          {folderUploadBatch.folderName}
                        </div>
                        <div
                          className={styles.folderUploadBatchProgressText}
                          role="status"
                          aria-live="polite"
                        >
                          {folderUploadBatch.isComplete
                            ? hasUnresolvedFolderUploadFailures
                              ? folderUploadBatch.failedFilenames.length ===
                                folderUploadBatch.totalFileCount
                                ? `Failed to upload "${folderUploadBatch.folderName}" (${folderUploadBatch.totalFileCount} of ${folderUploadBatch.totalFileCount})`
                                : `Uploaded "${folderUploadBatch.folderName}" with ${folderUploadBatch.failedFilenames.length} failure(s)`
                              : `Uploaded "${folderUploadBatch.folderName}"`
                            : `Uploading "${folderUploadBatch.folderName}" (${folderUploadBatch.completedFileCount} of ${folderUploadBatch.totalFileCount})...`}
                        </div>
                      </div>
                      <div className={styles.typeTagCell} />
                      {!folderUploadBatch.isComplete && (
                        <Spinner
                          size="tiny"
                          className={styles.fileSpinner}
                          aria-label={`Uploading folder "${folderUploadBatch.folderName}", ${folderUploadBatch.completedFileCount} of ${folderUploadBatch.totalFileCount} files complete`}
                        />
                      )}
                      {hasUnresolvedFolderUploadFailures && (
                        <button
                          type="button"
                          className={styles.folderUploadFailureIconButton}
                          onClick={handleToggleFolderUploadFailureList}
                          aria-label={
                            isFolderUploadFailureListExpanded
                              ? "Hide failed file list"
                              : "Show failed file list"
                          }
                          aria-expanded={isFolderUploadFailureListExpanded}
                        >
                          {isFolderUploadFailureListExpanded ? (
                            <ChevronUp24Regular aria-hidden="true" />
                          ) : (
                            <ChevronDown24Regular aria-hidden="true" />
                          )}
                        </button>
                      )}
                    </div>

                    {hasUnresolvedFolderUploadFailures &&
                      isFolderUploadFailureListExpanded && (
                        <div className={styles.folderUploadFailureList}>
                          <div className={styles.folderUploadFailureListHeader}>
                            <Text
                              className={styles.folderUploadFailureListTitle}
                            >
                              Failed to upload{" "}
                              {folderUploadBatch.failedFilenames.length} file
                              {folderUploadBatch.failedFilenames.length === 1
                                ? ""
                                : "s"}
                            </Text>
                            <button
                              type="button"
                              className={styles.folderUploadFailureIconButton}
                              onClick={handleDismissFolderUploadFailures}
                              aria-label="Dismiss failed file list"
                            >
                              <Dismiss24Regular aria-hidden="true" />
                            </button>
                          </div>
                          <ul className={styles.folderUploadFailureListItems}>
                            {folderUploadBatch.failedFilenames.map(
                              (failedFilename) => (
                                <li
                                  key={failedFilename}
                                  className={styles.folderUploadFailureListItem}
                                  title={failedFilename}
                                >
                                  {failedFilename}
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                  </div>
                )}

                {filePickerUploadingFiles.map((uploadFile) => (
                  <div
                    key={uploadFile.id}
                    role="listitem"
                    className={mergeClasses(
                      styles.fileRow,
                      uploadFile.status === "uploading" &&
                        styles.fileRowDisabled,
                    )}
                  >
                    <div className={styles.checkboxWrapper}></div>
                    <div
                      className={mergeClasses(
                        styles.fileIconWrapper,
                        styles.fileIconDefault,
                      )}
                    >
                      <DocumentIcon size={20} />
                    </div>
                    <div className={styles.fileInfo}>
                      <Text className={styles.fileName}>
                        {getDisplayFilename(uploadFile.originalFilename)}
                      </Text>
                      <Text className={styles.fileMeta}>
                        {uploadFile.status === "uploading"
                          ? "Uploading..."
                          : uploadFile.status === "error"
                            ? "Upload failed"
                            : "Uploaded"}
                      </Text>
                    </div>
                    {uploadFile.status === "uploading" && (
                      <Spinner size="tiny" className={styles.fileSpinner} />
                    )}
                    {uploadFile.status === "error" && (
                      <Button
                        appearance="subtle"
                        icon={<Dismiss16Regular />}
                        className={styles.menuButton}
                        aria-label={`Dismiss failed upload for ${getDisplayFilename(uploadFile.originalFilename)}`}
                        onClick={() => handleDismissFailedUpload(uploadFile.id)}
                      />
                    )}
                    {uploadFile.status === "error" && (
                      <div
                        className={styles.failedUploadCountdownTrack}
                        aria-hidden="true"
                      >
                        <div className={styles.failedUploadCountdownFill} />
                      </div>
                    )}
                  </div>
                ))}

                {filteredDocuments.map((doc) => {
                  const typeInfo = getFileTypeInfo(doc.originalFilename);
                  const iconClass =
                    iconStyleMap[typeInfo.style] ?? styles.fileIconDefault;
                  const isSelected = selectedFilenames.has(doc.filename);
                  const isDeleting = deletingFilenames.has(doc.filename);
                  const fileNameId = `file-name-${doc.filename}`;

                  return (
                    <div
                      key={doc.filename}
                      role="listitem"
                      className={mergeClasses(
                        styles.fileRow,
                        isSelected && styles.fileRowSelected,
                        isDeleting && styles.fileRowDisabled,
                      )}
                    >
                      <div className={styles.checkboxWrapper}>
                        <Checkbox
                          className={styles.selectionCheckbox}
                          checked={isSelected}
                          onChange={(_, data) =>
                            handleToggleFileSelection(
                              doc.filename,
                              data.checked === true,
                            )
                          }
                          aria-label={`Select ${getDisplayFilename(doc.originalFilename)}`}
                        />
                      </div>
                      <div
                        className={mergeClasses(
                          styles.fileIconWrapper,
                          iconClass,
                        )}
                      >
                        <DocumentIcon size={20} />
                      </div>
                      <div className={styles.fileInfo}>
                        <Text id={fileNameId} className={styles.fileName}>
                          {getDisplayFilename(doc.originalFilename)}
                        </Text>
                        <Text className={styles.fileMeta}>
                          {typeInfo.label}
                          {isDeleting ? " · Deleting..." : ""}
                        </Text>
                      </div>
                      {isDeleting ? (
                        <Spinner size="tiny" className={styles.fileSpinner} />
                      ) : (
                        <>
                          <div className={styles.typeTagCell}>
                            <DocumentTypeTag
                              documentType={doc.documentType}
                              options={availableTags}
                              onSelect={(documentType) =>
                                handleSetDocumentType(doc, documentType)
                              }
                            />
                          </div>
                          <div className={styles.shareToggle}>
                            <Switch
                              className={styles.shareToggleSwitch}
                              checked={doc.isSharedWithClient}
                              onChange={() => handleToggleShareWithClient(doc)}
                              disabled={isSharingInFlight}
                              aria-labelledby={`${SHARE_COLUMN_HEADER_ID} ${fileNameId}`}
                            />
                          </div>
                          <Menu>
                            <MenuTrigger disableButtonEnhancement>
                              <Button
                                appearance="subtle"
                                icon={<MoreVertical24Regular />}
                                className={styles.menuButton}
                                aria-label="File actions"
                              />
                            </MenuTrigger>
                            <MenuPopover>
                              <MenuList>
                                <MenuItem
                                  icon={<ArrowDownload24Regular />}
                                  onClick={() => handleDownloadDocument(doc)}
                                >
                                  Download
                                </MenuItem>
                                <MenuItem
                                  icon={<Delete24Regular />}
                                  className={styles.menuItemDanger}
                                  onClick={() => setDeleteConfirmDoc(doc)}
                                >
                                  Delete
                                </MenuItem>
                              </MenuList>
                            </MenuPopover>
                          </Menu>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          </div>

          {deleteConfirmDoc && (
            <ActionDialog
              isVisible={true}
              title="Delete Document"
              message={`Are you sure you want to delete "${deleteConfirmDoc.originalFilename}"? This action cannot be undone.`}
              onConfirmation={handleConfirmDelete}
              onDismiss={() => setDeleteConfirmDoc(null)}
              confirmationMessage="Delete"
              dismissMessage="Cancel"
            />
          )}

          {deleteConfirmFolder && (
            <ActionDialog
              isVisible={true}
              title="Delete Folder"
              message={`Delete "${deleteConfirmFolder.split("/").pop()}" and all its contents? This action cannot be undone.`}
              onConfirmation={handleConfirmFolderDelete}
              onDismiss={() => setDeleteConfirmFolder(null)}
              confirmationMessage="Delete"
              dismissMessage="Cancel"
            />
          )}

          {bulkDeleteConfirmation && (
            <ActionDialog
              isVisible={true}
              title="Delete Files"
              message={`Are you sure you want to delete ${describeFileCount(selectedFilenames.size)}? This action cannot be undone.`}
              onConfirmation={handleBulkDeleteConfirm}
              onDismiss={() => setBulkDeleteConfirmation(false)}
              confirmationMessage="Delete"
              dismissMessage="Cancel"
            />
          )}

          {pendingBulkShareAction && (
            <ActionDialog
              isVisible={true}
              title={
                pendingBulkShareAction === "share"
                  ? "Share with Client"
                  : "Stop Sharing with Client"
              }
              message={
                pendingBulkShareAction === "share"
                  ? `Are you sure you want to share ${describeFileCount(selectedFilenames.size)} with the client?`
                  : `Are you sure you want to stop sharing ${describeFileCount(selectedFilenames.size)} with the client?`
              }
              onConfirmation={
                pendingBulkShareAction === "share"
                  ? handleBulkShareWithClient
                  : handleBulkUnshare
              }
              onDismiss={() => setPendingBulkShareAction(null)}
              confirmationMessage={
                pendingBulkShareAction === "share" ? "Share" : "Stop Sharing"
              }
              spinnerMessage={
                pendingBulkShareAction === "share"
                  ? "Sharing..."
                  : "Removing..."
              }
              dismissMessage="Cancel"
            />
          )}

          {pendingShareToggleDocument && (
            <ActionDialog
              isVisible={true}
              title={
                pendingShareToggleDocument.isSharedWithClient
                  ? "Stop Sharing with Client"
                  : "Share with Client"
              }
              message={
                pendingShareToggleDocument.isSharedWithClient
                  ? `Are you sure you want to stop sharing "${pendingShareToggleDocument.originalFilename}" with the client?`
                  : `Are you sure you want to share "${pendingShareToggleDocument.originalFilename}" with the client?`
              }
              onConfirmation={handleConfirmShareToggle}
              onDismiss={() => setPendingShareToggle(null)}
              confirmationMessage={
                pendingShareToggleDocument.isSharedWithClient
                  ? "Stop Sharing"
                  : "Share"
              }
              spinnerMessage={
                pendingShareToggleDocument.isSharedWithClient
                  ? "Removing..."
                  : "Sharing..."
              }
              dismissMessage="Cancel"
            />
          )}
        </div>
      </div>
    </div>
  );
};
